import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const KOREA_TIMEZONE = 'Asia/Seoul' as const;

/**
 * * 현재 시각(한국 시간 기준)이 지정된 시간 범위에 포함되는지 확인
 *
 * @param startDate - 시작 날짜 및 시간 (포함)
 * @param endDate - 종료 날짜 및 시간 (포함)
 * @returns 현재 시각이 범위에 포함되면 true, 아니면 false
 *
 * @example
 *
 * // 프로모션 기간 내에 있는지 확인 (시간까지 체크)
 * * const isActive = checkTodayInRange('2024-01-01 09:00:00', '2024-12-31 18:00:00');
 *  *
 * @throws {Error} 유효하지 않은 날짜가 제공된 경우
 */
export const checkTodayInRange = (
  startDate: string | Date,
  endDate: string | Date,
): boolean => {
  const start = parseKoreaDate(startDate);
  const end = parseKoreaDate(endDate);
  const now = getNowInKorea();

  validateDateRange(start, end);

  return (
    (now.isAfter(start) || now.isSame(start)) &&
    (now.isBefore(end) || now.isSame(end))
  );
};

/**
 * * 한국 시간 기준으로 현재 시각을 가져옴
 */
export const getNowInKorea = (): Dayjs => {
  return dayjs().tz(KOREA_TIMEZONE);
};

/**
 * * 날짜를 파싱하고 한국 시간대로 변환
 */
const parseKoreaDate = (date: string | Date): Dayjs => {
  const parsed = dayjs(date).tz(KOREA_TIMEZONE);

  if (!parsed.isValid()) {
    throw new Error(`유효하지 않은 날짜: ${date}`);
  }

  return parsed;
};

/**
 * * 시작 시간이 종료 시간보다 늦지 않은지 검증
 */
const validateDateRange = (start: Dayjs, end: Dayjs): void => {
  if (start.isAfter(end)) {
    throw new Error(
      `잘못된 시간 범위: 시작 시간(${start.format('YYYY-MM-DD HH:mm:ss')})이 종료 시간(${end.format('YYYY-MM-DD HH:mm:ss')})보다 늦습니다`,
    );
  }
};
